import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';
import { Icon } from '../shared/components/Icon';
import { readPreference, writePreference } from '../shared/lib/preferences';
import { useDocumentLink } from '../workspace/documentLink';
import {
  isNavigationSection,
  operationsNavigation,
  primaryNavigation,
  type NavigationEntry,
  type NavigationItem,
  type NavigationSection,
} from '../workspace/navigation';

const storageKey = 'varza.navigation.folded';

/**
 * The rail is drawn from two captioned groups, each holding plain destinations and
 * sections that fold. Groups and sections share one identifier space because they fold
 * the same way and are remembered in the same place.
 */
const navigationGroups = [
  { id: 'workspace', labelKey: 'nav.workspace', entries: primaryNavigation },
  { id: 'operations', labelKey: 'nav.operations', entries: operationsNavigation },
] as const;

/**
 * Only what the reader folded away is stored, so a section added to the rail in a later
 * release arrives open rather than hidden behind a preference nobody set.
 */
function readFolded(): ReadonlySet<string> {
  try {
    const parsed: unknown = JSON.parse(readPreference(storageKey) || '[]');
    return new Set(Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : []);
  } catch {
    return new Set();
  }
}

function writeFolded(folded: ReadonlySet<string>): void {
  writePreference(storageKey, JSON.stringify([...folded]));
}

/** The dashboard answers for itself alone; every other address also owns what sits under it. */
function coversAddress(pathname: string, address: string): boolean {
  if (address === '/console') return pathname === '/console';
  return pathname === address || pathname.startsWith(`${address}/`);
}

/**
 * A row stops matching at its own address once a sibling lives underneath it, so opening
 * the new-journal form lights that row rather than lighting Journals alongside it.
 */
function matchesExactly(item: NavigationItem, siblings: readonly NavigationItem[]): boolean {
  return item.to === '/console' || siblings.some((other) => other.to.startsWith(`${item.to}/`));
}

function NavigationRow({ item, siblings, row, collapsed, onNavigate }: {
  item: NavigationItem;
  siblings: readonly NavigationItem[];
  /** Position inside a folding section, which staggers the reveal. Absent for a top-level row. */
  row?: number;
  collapsed: boolean;
  onNavigate: () => void;
}) {
  const { t } = useTranslation();
  const openDocument = useDocumentLink();
  const label = t(item.labelKey);
  const nested = row !== undefined;
  return (
    <NavLink
      to={item.to}
      end={matchesExactly(item, siblings)}
      onClick={openDocument(item.to, onNavigate)}
      className={({ isActive }) => `nav-link${nested ? ' nav-link--child' : ''}${isActive ? ' nav-link--active' : ''}`}
      title={collapsed ? label : undefined}
      style={{ '--nav-row': row ?? 0 } as CSSProperties}
    >
      <Icon name={item.icon} />
      <span className={collapsed ? 'sr-only' : ''}>{label}</span>
    </NavLink>
  );
}

function NavigationBranch({ section, open, current, collapsed, onToggle, onNavigate }: {
  section: NavigationSection;
  open: boolean;
  /** True while the document on screen came from inside this section. */
  current: boolean;
  collapsed: boolean;
  onToggle: () => void;
  onNavigate: () => void;
}) {
  const { t } = useTranslation();
  const label = t(section.labelKey);
  const panelId = `nav-section-${section.id}`;
  return (
    <div className={`nav-branch${open ? ' nav-branch--open' : ''}${current ? ' nav-branch--current' : ''}`}>
      <button
        type="button"
        className="nav-link nav-branch__summary"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={panelId}
        title={collapsed ? label : undefined}
      >
        <Icon name={section.icon} />
        <span className={collapsed ? 'sr-only' : 'nav-branch__title'}>{label}</span>
        {collapsed ? null : <Icon name="chevron" className="icon icon--small nav-branch__chevron" />}
      </button>
      <div className="nav-branch__panel" id={panelId}>
        <div className="nav-branch__clip">
          <div className="nav-branch__links">
            {section.items.map((item, index) => (
              <NavigationRow key={item.to} item={item} siblings={section.items} row={index} collapsed={false} onNavigate={onNavigate} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * The rail itself.
 *
 * Folding is the reader's own preference and outlives the visit, with one exception: the
 * section holding whatever is on screen opens itself, so arriving from a link elsewhere
 * never leaves the current document with nothing lit beside it.
 */
export function SidebarNavigation({ collapsed, onNavigate, onExpandRail }: {
  collapsed: boolean;
  onNavigate: () => void;
  /** A rail narrowed to icons has no room for sub-links, so opening a section widens it. */
  onExpandRail: () => void;
}) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [folded, setFolded] = useState(readFolded);

  const setOpen = useCallback((id: string, open: boolean) => {
    setFolded((current) => {
      if (open === !current.has(id)) return current;
      const next = new Set(current);
      if (open) next.delete(id);
      else next.add(id);
      writeFolded(next);
      return next;
    });
  }, []);

  const owner = useMemo(() => {
    for (const group of navigationGroups) {
      for (const entry of group.entries) {
        if (!isNavigationSection(entry)) continue;
        if (entry.items.some((item) => coversAddress(pathname, item.to))) return { group: group.id, section: entry.id };
      }
    }
    return undefined;
  }, [pathname]);

  useEffect(() => {
    if (!owner) return;
    setOpen(owner.group, true);
    setOpen(owner.section, true);
  }, [owner, setOpen]);

  return (
    <div className="sidebar__navigation">
      {navigationGroups.map((group) => {
        const title = t(group.labelKey);
        // A rail narrowed to icons carries no captions, so its groups cannot be folded shut.
        const groupOpen = collapsed || !folded.has(group.id);
        const siblings = group.entries.filter((entry): entry is NavigationItem => !isNavigationSection(entry));
        return (
          <div key={group.id} className={`nav-group${groupOpen ? '' : ' nav-group--folded'}`}>
            {collapsed ? null : (
              <button
                type="button"
                className="nav-group__label"
                onClick={() => setOpen(group.id, !groupOpen)}
                aria-expanded={groupOpen}
                aria-controls={`nav-group-${group.id}`}
              >
                <span>{title}</span>
                <Icon name="chevron" className="icon icon--small nav-group__chevron" />
              </button>
            )}
            <div className="nav-group__panel" id={`nav-group-${group.id}`}>
              <div className="nav-group__clip">
                <nav aria-label={title}>
                  {group.entries.map((entry: NavigationEntry) => {
                    if (!isNavigationSection(entry)) {
                      return <NavigationRow key={entry.to} item={entry} siblings={siblings} collapsed={collapsed} onNavigate={onNavigate} />;
                    }
                    const open = !collapsed && !folded.has(entry.id);
                    return (
                      <NavigationBranch
                        key={entry.id}
                        section={entry}
                        open={open}
                        current={entry.items.some((item) => coversAddress(pathname, item.to))}
                        collapsed={collapsed}
                        onToggle={() => {
                          if (collapsed) {
                            onExpandRail();
                            setOpen(entry.id, true);
                            return;
                          }
                          setOpen(entry.id, !open);
                        }}
                        onNavigate={onNavigate}
                      />
                    );
                  })}
                </nav>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
